import { z } from 'zod';

import { EmailSchema } from './common.js';

/**
 * Careers: public job-application submission + admin enquiry shapes.
 *
 * The public website "Apply now" popup posts a CareerApplicationSubmit to
 * `POST /website/careers/apply`. The API validates it, stores the CV in S3,
 * and creates a JobApplication row that the admin lists under the career
 * page's "Enquiries" tab. This schema is the single source of truth shared by
 * the API service (validation) and the website form (react-hook-form resolver).
 */

/**
 * Lifecycle of a job application as triaged by the admin. Mirrors the existing
 * Prisma `WebsiteCareerApplicationStatus` enum (the model we persist into).
 */
export const CareerApplicationStatusSchema = z.enum([
  'RECEIVED',
  'IN_REVIEW',
  'INTERVIEW',
  'OFFER',
  'REJECTED',
  'WITHDRAWN',
]);
export type CareerApplicationStatus = z.infer<typeof CareerApplicationStatusSchema>;

/** CV upload caps + accepted types (mirrors the imports 10MB convention). */
export const CV_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
export const CV_ACCEPTED_MIME = [
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
] as const;
export const CV_ACCEPT_ATTR = '.pdf,.doc,.docx';

/**
 * Public apply payload. The CV rides base64-encoded in the JSON body (the API
 * has no multipart handling; this mirrors the imports upload pattern and fits
 * the 20MB express.json limit). The server re-validates size + magic bytes.
 */
export const CareerApplicationSubmitSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: EmailSchema,
  phone: z.string().trim().min(8).max(20),
  /** Role applied for; auto-filled + disabled in the website popup. */
  positionTitle: z.string().trim().min(1).max(160),
  /** The OPEN_POSITIONS section item id, when known (best-effort linkage). */
  positionId: z.string().max(64).optional(),
  cvFileName: z.string().trim().min(1).max(200),
  /** Base64-encoded CV bytes (no data: URI prefix). */
  cvBase64: z.string().min(1),
});
export type CareerApplicationSubmit = z.infer<typeof CareerApplicationSubmitSchema>;

/** Admin list row for the Enquiries tab. CV bytes are fetched separately. */
export const CareerApplicationDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  positionTitle: z.string(),
  status: CareerApplicationStatusSchema,
  cvFileName: z.string(),
  createdAt: z.string(),
});
export type CareerApplicationDetail = z.infer<typeof CareerApplicationDetailSchema>;

/** Admin status-update payload. */
export const UpdateCareerApplicationSchema = z.object({
  status: CareerApplicationStatusSchema,
});
export type UpdateCareerApplication = z.infer<typeof UpdateCareerApplicationSchema>;

/* ------------------------------------------------------------------ *
 * HR dashboard (2026-08-04)                                          *
 * ------------------------------------------------------------------ */

/**
 * Recruitment snapshot for the HR desk's own dashboard.
 *
 * HR_MANAGER / HR_EXECUTIVE hold `web.career.*` plus read on the org masters
 * and nothing else (ADR-0063), so the generic sales dashboard rendered a page
 * of empty KPIs for them. This is the data they can actually act on: the
 * application funnel, what is waiting on a human, and the position breakdown.
 *
 * Deliberately excludes headcount / joiners / exits - that lives on `users`,
 * which stays ADMIN-only by policy.
 */
export const HrDashboardFunnelSchema = z.object({
  received: z.number().int().nonnegative(),
  inReview: z.number().int().nonnegative(),
  interview: z.number().int().nonnegative(),
  offer: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  withdrawn: z.number().int().nonnegative(),
});
export type HrDashboardFunnel = z.infer<typeof HrDashboardFunnelSchema>;

export const HrDashboardPositionSchema = z.object({
  positionTitle: z.string(),
  total: z.number().int().nonnegative(),
  /** Still in play: RECEIVED + IN_REVIEW + INTERVIEW + OFFER. */
  active: z.number().int().nonnegative(),
});
export type HrDashboardPosition = z.infer<typeof HrDashboardPositionSchema>;

export const HrDashboardSchema = z.object({
  /** Applications by status, all time. */
  funnel: HrDashboardFunnelSchema,
  totals: z.object({
    all: z.number().int().nonnegative(),
    /** Still in play - excludes REJECTED / WITHDRAWN. */
    active: z.number().int().nonnegative(),
    last7Days: z.number().int().nonnegative(),
    last30Days: z.number().int().nonnegative(),
  }),
  /** Queues that need a human. Drives the "Needs attention" tiles. */
  needsAttention: z.object({
    /** RECEIVED - nobody has triaged these yet. */
    unscreened: z.number().int().nonnegative(),
    /** IN_REVIEW with no status change for over 7 days. */
    staleInReview: z.number().int().nonnegative(),
    /** INTERVIEW stage, awaiting an outcome for over 14 days. */
    awaitingOutcome: z.number().int().nonnegative(),
  }),
  /** Live roles on the careers page (OPEN_POSITIONS section items). */
  openPositions: z.number().int().nonnegative(),
  /** Applications per role, busiest first. */
  byPosition: z.array(HrDashboardPositionSchema),
  /** Newest applications for the "Recent applicants" table. */
  recent: z.array(CareerApplicationDetailSchema),
  /**
   * Org-master counts. Manager-only in the UI (they hold write on these);
   * the API returns them for any HR caller since both hold read.
   */
  orgMasters: z.object({
    branches: z.number().int().nonnegative(),
    subBranches: z.number().int().nonnegative(),
    departments: z.number().int().nonnegative(),
  }),
});
export type HrDashboard = z.infer<typeof HrDashboardSchema>;
